import type { GenerateRequest, SeedsMapInput } from "@/types/creativesphere";
import type { ProductGroupType } from "@/types/propagation";

import {
  SEED_TYPE_ORDER,
  type PickedSeeds,
} from "@/features/GenLab/components/pickedSeeds";

export function buildGenerateRequest(
  siteId: string,
  picked: PickedSeeds,
  model: string,
): GenerateRequest {
  const seed_refs = SEED_TYPE_ORDER.map((type) => picked[type]?.id).filter(
    (slug): slug is string => Boolean(slug),
  );
  const aspect_ratio = picked.composition?.format;
  const product_image_url = picked.product?.imageUrl;
  return {
    site_id: siteId,
    seed_refs,
    base_prompt: "",
    model,
    ...(product_image_url ? { product_image_url } : {}),
    ...(aspect_ratio ? { options: { aspect_ratio } } : {}),
  };
}

/** Args for {@link buildPilotGenerateRequest}. */
export interface BuildPilotGenerateRequestArgs {
  siteId: string;
  /** Full seed snapshot, typically taken straight from the chosen blueprint. */
  seedsMap: SeedsMapInput;
  /** Free-text prompt; sent as proto `base_prompt`. */
  additionalPrompt?: string;
  // Redundant with the product seed's contract, but the deployed compiler still reads only this field.
  productImageUrl?: string;
  model?: string;
  /** Associates the resulting generation with a propagation (the pilot). */
  propagationId?: string;
  /** With propagationId, ties the generation to a single propagation item. */
  propagationItemId?: string;
  groupId?: string;
  groupType?: ProductGroupType;
}

/** Builds the `POST /v1/generate` body for the blueprint/pilot path. */
export function buildPilotGenerateRequest({
  siteId,
  seedsMap,
  additionalPrompt,
  productImageUrl,
  model,
  propagationId,
  propagationItemId,
  groupId,
  groupType,
}: BuildPilotGenerateRequestArgs): GenerateRequest {
  return {
    site_id: siteId,
    seeds_map: seedsMap,
    ...(model ? { model } : {}),
    ...(additionalPrompt ? { base_prompt: additionalPrompt } : {}),
    ...(productImageUrl ? { product_image_url: productImageUrl } : {}),
    ...(propagationId ? { propagation_id: propagationId } : {}),
    ...(propagationItemId ? { propagation_item_id: propagationItemId } : {}),
    ...(groupId ? { group_id: groupId } : {}),
    ...(groupType ? { group_type: groupType } : {}),
  };
}
